// src/services/api.js
import axios from "axios";
import { refreshToken, shouldSkipRefresh } from "../utils/auth";
import { API_BASE } from "../config";
import { ensureCsrf } from "./csrf";
import { getCookie } from "../lib/cookie";

export { API_BASE } from "../config";

const CSRF_URL = "/api/auth/csrf";

// Let Axios copy the CSRF cookie → header verbatim.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
});

const isUnsafe = (m) => !/^(GET|HEAD|OPTIONS|TRACE)$/i.test(m || "GET");

api.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toUpperCase();

  // Prime CSRF cookie before any unsafe request
  if (isUnsafe(method) && !document.cookie.includes("XSRF-TOKEN=")) {
    await ensureCsrf();
  }

  // Backstop: ensure header is present (Axios usually adds it automatically)
  if (isUnsafe(method)) {
    const xsrf = getCookie("XSRF-TOKEN");
    if (xsrf) {
      config.headers = config.headers || {};
      if (!config.headers["X-XSRF-TOKEN"]) {
        config.headers["X-XSRF-TOKEN"] = xsrf;
      }
    }
  }

  const url = config?.url || "";
  const token = localStorage.getItem("accessToken");

  const isOpenAuthRoute =
    /\/api\/auth\/(login|register|refresh|logout|forgot-password|reset-password|verify|resend-code|csrf|gdb-signup)\b/.test(
      url
    );

  if (token && !isOpenAuthRoute) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// ---- single-flight refresh ----
let refreshPromise = null;
const getFreshToken = async () => {
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status || 0;
    const url = original.url || "";
    const isRefresh = url.includes("/api/auth/refresh");

    // Stop the /api/auth/csrf self-retry storm
    if (status === 403 && !original.__retried && !url.includes(CSRF_URL)) {
      original.__retried = true;
      try {
        await ensureCsrf();
        return api(original);
      } catch {
        /* fall through */
      }
    }

    const hasToken = !!localStorage.getItem("accessToken");
    const onNoRefreshPage =
      typeof window !== "undefined" && shouldSkipRefresh(window.location.pathname);

    const shouldTryRefresh =
      !original._retry &&
      !isRefresh &&
      (status === 401 || !error?.response) &&
      hasToken &&
      !onNoRefreshPage;

    if (shouldTryRefresh) {
      original._retry = true;
      try {
        const newToken = await getFreshToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (e) {
        localStorage.removeItem("accessToken");
        window.location.href = "/login";
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
